import type { RequiredFileModel, Stored, UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { useImageViewer } from "@overmap-ai/forms"
import { cx } from "class-variance-authority"
import { useCallback } from "react"

import { useFetchFileFromFileModel } from "../../hooks/utils"
import { AppSDK } from "../../sdk"
import type { RootState } from "../../store"
import { genericMemo } from "../../utils/optimization"
import { ImageFileThumbnail } from "./ImageFileThumbnail"

interface ImageAttachmentThumbnailProps<TAttachment extends UUID<RequiredFileModel>> {
	attachment: Stored<TAttachment>
	service: BaseOfflineFileModelApiService<TAttachment["uuid"], TAttachment, RootState, AppSDK>
}

export const ImageAttachmentThumbnail = genericMemo(function <TAttachment extends UUID<RequiredFileModel>>(
	props: ImageAttachmentThumbnailProps<TAttachment>,
) {
	const { attachment, service } = props

	const [fetchedFile, failed] = useFetchFileFromFileModel(attachment, service)
	const openImageViewer = useImageViewer()

	const handleView = useCallback(() => {
		if (!fetchedFile) return

		openImageViewer((closeFileViewer) => ({
			file: fetchedFile,
			onClose: closeFileViewer,
		}))
	}, [fetchedFile, openImageViewer])

	return (
		<ImageFileThumbnail
			className={cx({
				"cursor-pointer": !!fetchedFile,
			})}
			onClick={handleView}
			file={fetchedFile}
			errored={failed}
		/>
	)
})
